import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { IWeekDay } from '@/types/calendar';
import type {
  ICalendarPosition,
  IScheduleBlock,
  IScheduleBlockAvailable,
  ISortedAvailableShift,
  ISortedGroupedSchedule,
  ISortedShift,
} from '@/types/schedule';
import type { Hue } from '@/types/styles';

import { colors } from '@/styles/colors';
import { useAppDispatch, useAppSelector, useMouseClick, useMouseClickOutside, useTimezone } from '@/hooks';
import { HALF_AN_HOUR_BLOCK_SPAN, HOUR_IN_PX, PADDING_WEEKDAY } from '@/config/variables';
import { clearActiveModalShiftId, setActiveModalShiftId } from '@/store/slices/utilsSlice';
import { selectAvailableShifts } from '@/store/selectors/scheduleSelectors';
import { formatStartEndTime, getBlockSettings, getCalendarPosition } from '@/helpers';

export interface BaseShiftProps {
  block: IScheduleBlock | IScheduleBlockAvailable;
  blockColor: Hue;
  contract?: ISortedShift<ISortedGroupedSchedule>;
  className?: string; // https://styled-components.com/docs/basics#styling-any-component
  icon?: string;
  modal?: <
    T extends {
      parentRef: React.MutableRefObject<HTMLDivElement>;
      ref: React.RefObject<HTMLDivElement>;
      handleClose?: () => void;
    },
  >(
    parentRef: React.MutableRefObject<HTMLDivElement>,
    ref: React.RefObject<HTMLDivElement>,
    handleClose?: () => void,
  ) => React.ReactElement<T>;
  children?: React.ReactNode;
  isSelected?: boolean;
  onClick?: () => void; // used only by available shift (not published)
  isDlsBlocked?: boolean;
  day: IWeekDay;
}

const baseShiftSelector = createSelector(selectAvailableShifts, availableShifts => ({
  availableShifts,
}));

const BaseShiftWithRef = React.forwardRef<HTMLDivElement, BaseShiftProps>(function BaseShift(
  { block, blockColor, contract, children, className, icon, onClick, modal, isDlsBlocked, day },
  forwardedRef,
): React.ReactElement {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const { timezoneName } = useTimezone();
  const { activeModalShiftId } = useAppSelector(state => state.utils);
  const dispatch = useAppDispatch();

  const { availableShifts } = useAppSelector(baseShiftSelector);

  const isAvailableShift = !Boolean(contract);

  const node = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  useMouseClickOutside(modalRef, () => isFocused && !isAvailableShift && toggleIsFocused(false));
  useMouseClick(node, (event: MouseEvent) => {
    const path: HTMLDivElement[] = (event as any).path || (event.composedPath && event.composedPath()) || [];
    const isNodeModal = Boolean(path.find(item => item.classList?.contains('base-shift-modal')));
    !isNodeModal && !isAvailableShift && toggleIsFocused(!isFocused);
  });

  // set proper block duration time (for labels)
  const originalBlockDuration = useMemo(() => {
    const blocks = {
      startBlock: block.startBlock,
      endBlock: block.endBlock,
    };
    if ((block as ISortedAvailableShift)?.splitStart || (block as ISortedAvailableShift)?.splitEnd) {
      const shift = availableShifts.find(shift => shift.shiftId === block.shiftId);
      if (shift) {
        blocks.startBlock = shift?.startBlock;
        blocks.endBlock = shift?.endBlock;
      }
    }
    return blocks;
  }, [availableShifts, block]);

  const calendarPosition = useMemo<ICalendarPosition>(
    () => getCalendarPosition(originalBlockDuration.startBlock, originalBlockDuration.endBlock, timezoneName, day),
    [day, timezoneName, originalBlockDuration.endBlock, originalBlockDuration.startBlock],
  );

  // modify block duration for split
  if (contract && block.endBlock > contract.itemData.endBlock) {
    const diff = (Math.min(block.endBlock, contract.itemData.endBlock) + 1 - originalBlockDuration.startBlock) / 2;
    calendarPosition.blockDuration = diff + HOUR_IN_PX;
  }

  const scheduleDuration =
    contract &&
    getBlockSettings(
      contract.shifts[0].startBlock,
      contract.shifts[contract.shifts.length - 1].endBlock,
      timezoneName,
      day,
    ).blockDuration;

  const handleCloseOfferModal = (): void => {
    setTimeout(() => toggleIsFocused(false), 0);
  };

  const toggleIsFocused = (status: boolean) => {
    if (!contract) return;
    setIsFocused(status);
  };

  useEffect(() => {
    if (isFocused && Boolean(modal) && activeModalShiftId === null) {
      dispatch(setActiveModalShiftId(block.shiftId));
    }

    if (!isFocused && activeModalShiftId === block.shiftId) {
      dispatch(clearActiveModalShiftId());
    }
  }, [isFocused, modal, block.shiftId, dispatch]);

  return (
    <BaseShiftContainer
      className={className}
      blockColor={blockColor}
      blockDuration={calendarPosition.blockDuration}
      scheduleDuration={scheduleDuration || 0}
      minutesFromMidnight={calendarPosition.minutesFromMidnight}
      focused={isFocused}
      ref={ref => {
        node.current = ref;

        if (typeof forwardedRef === 'function') {
          forwardedRef(ref);
          return;
        }

        if (forwardedRef !== null) {
          forwardedRef.current = ref;
        }
      }}
      onClick={onClick}
    >
      {modal &&
        ((activeModalShiftId && isFocused) || isDlsBlocked) &&
        modal(node as React.MutableRefObject<HTMLDivElement>, modalRef, handleCloseOfferModal)}
      <BaseShiftLabel className="title">
        {icon && <BaseShiftIcon className="title-icon" src={icon} blockColor={blockColor} />}
        {children}
      </BaseShiftLabel>
      <BaseShiftLabel className="hour">
        {formatStartEndTime(calendarPosition.startAt, calendarPosition.endAt)}
      </BaseShiftLabel>
    </BaseShiftContainer>
  );
});

export { BaseShiftWithRef as BaseShift };

const BaseShiftContainer = styled.div<{
  focused: boolean;
  scheduleDuration: number;
  blockColor: Hue;
  blockDuration: number;
  minutesFromMidnight: number;
}>`
  display: flex;
  flex-direction: row;
  flex-grow: ${props => props.blockDuration / HALF_AN_HOUR_BLOCK_SPAN};
  align-items: center;
  justify-content: space-between;
  font-variant-numeric: tabular-nums;
  user-select: none;

  position: relative;

  color: ${props => (props.focused ? colors[props.blockColor]['c800'] : '#fff')};
  background-color: ${props => colors[props.blockColor][props.focused ? 'c50' : 'c300']};
  border-radius: 6px;
  white-space: nowrap;

  width: 100%;

  &:not(.available-shift-item) {
    height: ${props => props.blockDuration - 2 * PADDING_WEEKDAY}px;
    margin-bottom: 3px;
  }

  &:last-child {
    margin-bottom: 0;
  }

  /* default styles for shifts in columns (available + vto extra content shifts) */

  [data-row-length='1'] & {
    .hour,
    .title {
      display: flex;
    }
  }

  :not([data-row-length='1'] &) {
    .hour {
      display: none;
    }

    .title {
      display: flex;
      justify-content: center;
      flex-grow: 1;
      padding-left: 0;
    }
  }
`;

const BaseShiftLabel = styled.div`
  display: flex;
  font-size: 10px;
  line-height: 12px;

  &.title {
    font-weight: 700;
    padding-left: 10px;

    &:empty {
      display: none;

      & + .hour {
        padding-left: 10px;
      }
    }
  }

  &.hour {
    font-weight: 500;
    padding-right: 10px;
    letter-spacing: -0.5px;
  }
`;

const BaseShiftIcon = styled.div<{ src: string; blockColor: Hue }>`
  margin-right: 4px;
  background-color: ${props => colors[props.blockColor]['c800']};
  mask-image: url(${props => props.src});
  mask-repeat: no-repeat;
  mask-position: center center;
  mask-size: contain;
  width: 13px;
  height: 12px;
`;
